import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useFileViewer } from "@/components/app/sending/file-viewer";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { putToSignedUrl } from "@/lib/files/signed-upload";
import { MyHiringDocuments } from "./-components/my-hiring-documents";
import { MyRequirementCard } from "./-components/my-requirement-card";
import { SignaturesNeeded } from "./-components/signatures-needed";

export const Route = createFileRoute("/_authenticated/_workforce/hr/my-documents")({
  component: MyDocuments,
});

const failed = (error: Error) => toast.error(errorWords(error));

/** The employee's own side of the file: what HR still needs from them, what they have already
 * handed in, and where each of those stands. */
function MyDocuments() {
  const queryClient = useQueryClient();
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const requirements = useQuery(orpc.workforce.employeeDocuments.myRequirements.queryOptions({}));

  const record = useMutation(
    orpc.workforce.employeeDocuments.record.mutationOptions({
      onSuccess: async () => {
        toast.success("Document uploaded");
        await queryClient.invalidateQueries({ queryKey: orpc.workforce.employeeDocuments.key() });
      },
      onError: failed,
    }),
  );
  const { openFile, fileViewer } = useFileViewer();
  const download = useMutation(
    orpc.workforce.employeeDocuments.downloadUrl.mutationOptions({
      // Read in place, not handed to the browser to save: see FileViewer.
      onSuccess: (result) => openFile(result),
      onError: failed,
    }),
  );

  /* The bytes go straight to storage and only the path comes back here, so a
     scanned licence never travels through the API. */
  const upload = async (requirementId: string, file: File, expiresAt: string | null) => {
    const contentType = file.type || "application/octet-stream";
    setUploadingFor(requirementId);
    try {
      const target = await orpc.workforce.employeeDocuments.uploadUrl.call({ contentType });
      await putToSignedUrl(
        target.url,
        file,
        contentType,
        (status) => `The file could not be stored (${status}). Try again, or hand a copy to HR.`,
      );
      record.mutate({ requirementId, filePath: target.objectPath, fileName: file.name, expiresAt });
    } catch (error) {
      failed(error as Error);
    } finally {
      setUploadingFor(null);
    }
  };

  const outstanding = (requirements.data ?? []).filter(
    (requirement) => requirement.status !== "approved",
  ).length;

  return (
    <>
      <PageHeader
        title="My documents"
        meta={
          /* "Everything asked of you is on file" above "Nothing is being asked
             of you" said the same nothing twice, and nearly contradicted it. */
          requirements.data && requirements.data.length > 0
            ? outstanding === 0
              ? "Everything asked of you is on file."
              : `${outstanding} still outstanding.`
            : "What the building needs from you."
        }
      />

      <SignaturesNeeded />

      {/* The offer they accepted and the forms they signed on the way in were
          filed correctly and read by nothing. */}
      <MyHiringDocuments />

      {requirements.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : requirements.isError ? (
        <ReadFailed
          message="Could not load your documents."
          error={requirements.error}
          onRetry={() => void requirements.refetch()}
          retrying={requirements.isFetching}
        />
      ) : requirements.data.length === 0 ? (
        <EmptyState
          title="Nothing is being asked of you"
          description="Documents HR needs from you will appear here."
        />
      ) : (
        <div className="space-y-3">
          {requirements.data.map((requirement) => (
            <MyRequirementCard
              key={requirement.id}
              requirement={requirement}
              uploading={uploadingFor === requirement.id || record.isPending}
              onUpload={(file, expiresAt) => void upload(requirement.id, file, expiresAt)}
              onOpenDocument={(documentId) => download.mutate({ documentId })}
            />
          ))}
        </div>
      )}
      {fileViewer}
    </>
  );
}
